import qs from 'qs';
import { get as getProperty, isEmpty, merge } from 'lodash';
import ndjsonStream from 'can-ndjson-stream';

export const TOKEN_STORAGE_KEY = 'auth_token';

const COMMON_HEADERS = {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'cache-control': 'no-cache'
  }
};

const DEFAULT_ERROR_MESSAGE = 'Error Requesting the Resource';

class RequestError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export const parseQuery = options => {
  const query = getProperty(options, 'params', {});
  return isEmpty(query) ? '' : `?${qs.stringify(query)}`;
};

const parseError = err => ({
  message: getProperty(err, 'message', ''),
  status: getProperty(err, 'status', 0)
});

const checkStatus = (res, body) => {
  if (res.status < 200 || res.status >= 400) {
    throw new RequestError(
      getProperty(body, 'message', DEFAULT_ERROR_MESSAGE),
      res.status
    );
  }
};

const request = async (url, options) => {
  try {
    const query = parseQuery(options);
    const response = await fetch(`${url}${query}`, options);
    const responseBody = await response.json();

    checkStatus(response, responseBody);

    return { response, data: responseBody };
  } catch (err) {
    console.log(err);
    return Promise.reject(parseError(err));
  }
};

const post = async (url, body, overrides = {}) => {
  const postOverrides = { ...overrides, method: 'POST' };
  const options = merge({ body }, COMMON_HEADERS, postOverrides);

  if (options.headers.Accept === 'application/octet-stream') {
    try {
      const query = parseQuery(options);
      const response = await fetch(`${url}${query}`, options);
      // TODO: review get method
      checkStatus(response)
      return response;
    } catch (err) {
      console.log(err);
      return Promise.reject(parseError(err));
    }
  }

  return request(url, options);
};

const getStream = async (url, overrides = {}) => {
  const options = merge({}, COMMON_HEADERS, overrides);

  try {
    const query = parseQuery(options);
    const response = await fetch(`${url}${query}`, options);
    checkStatus(response);
    const exampleReader = ndjsonStream(response.body).getReader();

    let result;
    const data = [];
    while (!result || !result.done) {
      // eslint-disable-next-line no-await-in-loop
      result = await exampleReader.read();
      if (result.value) {
        data.push(result.value);
      }
    }

    return { data };
  } catch (err) {
    console.log(err);
    return Promise.reject(parseError(err));
  }
};

const get = async (url, overrides = {}) => {
  const options = merge({}, COMMON_HEADERS, overrides);

  if (options.headers.Accept === 'application/octet-stream') {
    try {
      const query = parseQuery(options);
      const response = await fetch(`${url}${query}`, options);
      // TODO: review get method
      checkStatus(response)
      return response;
    } catch (err) {
      console.log(err);
      return Promise.reject(parseError(err));
    }
  }
  return request(url, options);
};

const put = (url, body, overrides = {}) => {
  const postOverrides = { ...overrides, method: 'PUT' };
  const options = merge({ body }, COMMON_HEADERS, postOverrides);

  return request(url, options);
};

const del = (url, body, overrides = {}) => {
  const postOverrides = { ...overrides, method: 'DELETE' };
  const options = merge({ body }, COMMON_HEADERS, postOverrides);

  return request(url, options);
};

export const withAuth = req => async (url, ...args) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const authOptions = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const options = merge({}, args[args.length - 1], authOptions);
  const params = [options];

  switch (true) {
    case req === get || req === getStream:
      break;

    case req === post: {
      params.unshift(getProperty(args, '0', null));
      break;
    }

    case req === put: {
      params.unshift(getProperty(args, '0', null));
      break;
    }

    case req === del: {
      params.unshift(getProperty(args, '0', null));
      break;
    }

    default:
      throw new RequestError('Unknown Method', 0);
  }

  return req(url, ...params);
};

export default {
  get,
  getStream,
  post,
  put,
  del
};
